import * as React from "react"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

/**
 * FormModal - Reusable modal component for all forms in Teacher Portal
 * 
 * Features:
 * - Backdrop overlay with blur effect
 * - Smooth fade-in/out animations
 * - Keyboard support (ESC to close)
 * - Click-outside-to-close
 * - Loading/Error states
 * - Responsive design
 */

export function FormModal({
    isOpen,
    onClose,
    onSave,
    title,
    children,
    saveLabel = "Save",
    cancelLabel = "Cancel",
    isLoading = false,
    size = "md",
    className,
    ...props
}) {
    const [isSaving, setIsSaving] = React.useState(false)

    // Handle ESC key press
    React.useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape" && isOpen && !isLoading && !isSaving) {
                onClose()
            }
        }

        if (isOpen) {
            document.addEventListener("keydown", handleEscape)
            // Prevent body scroll when modal is open
            document.body.style.overflow = "hidden"
        }

        return () => {
            document.removeEventListener("keydown", handleEscape)
            document.body.style.overflow = "unset"
        }
    }, [isOpen, onClose, isLoading, isSaving])

    // Handle save action
    const handleSave = async () => {
        if (isSaving || isLoading) return

        setIsSaving(true)
        try {
            await onSave()
        } catch (error) {
            console.error("Save error:", error)
        } finally {
            setIsSaving(false)
        }
    }

    // Handle backdrop click
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && !isLoading && !isSaving) {
            onClose()
        }
    }

    // Size variants
    const sizeClasses = {
        sm: "max-w-md",
        md: "max-w-2xl",
        lg: "max-w-4xl",
        xl: "max-w-6xl",
    }

    if (!isOpen) return null

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
            onClick={handleBackdropClick}
            {...props}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Modal */}
            <div
                className={cn(
                    "relative w-full bg-gradient-to-b from-[#F8F5F0] to-[#F5F1EA] rounded-3xl shadow-2xl border border-[#E0D4BF] overflow-hidden animate-in zoom-in-95 duration-200",
                    sizeClasses[size],
                    className
                )}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#E0D4BF] bg-gradient-to-r from-[#F0E8D8] to-[#EDE3D2]">
                    <h2 className="text-xl font-light text-[#2B2B2B]">{title}</h2>
                    <button
                        onClick={onClose}
                        disabled={isLoading || isSaving}
                        className="p-2 rounded-full hover:bg-[#E0D4BF]/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Close modal"
                    >
                        <X className="w-5 h-5 text-[#6B6B6B]" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                    {children}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E0D4BF] bg-gradient-to-r from-[#F0E8D8] to-[#EDE3D2]">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isLoading || isSaving}
                        className="border-[#C9B08A] text-[#6B6B6B] hover:bg-[#E0D4BF]/50 font-light"
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isLoading || isSaving}
                        className="bg-black hover:bg-gray-800 text-white font-light border border-gray-700"
                    >
                        {(isLoading || isSaving) && (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        )}
                        {saveLabel}
                    </Button>
                </div>
            </div>
        </div>
    )
}

FormModal.displayName = "FormModal"
